import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import ChooseSection from "../../components/ChooseSection/ChooseSection";
import TestimonialCarousel from "../../pages/Testimonials/TestimonialCarousel";
import SectionSeparator from "../../components/Separator/SectionSeparator";
import "./Home.css";
import AppNavbar from "../../components/AppNavbar/AppNavbar";


// Animated counter hook
const useCountUp = (target, isActive, duration = 1800) => {
    const [count, setCount] = useState(0);
    useEffect(() => {
        if (!isActive) return;
        const numeric = parseFloat(target.replace(/[^0-9.]/g, ""));
        const suffix = target.replace(/[0-9.]/g, "");
        let start = null;
        const step = (ts) => {
            if (!start) start = ts;
            const progress = Math.min((ts - start) / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(Math.floor(eased * numeric) + suffix);
            if (progress < 1) requestAnimationFrame(step);
            else setCount(target);
        };
        requestAnimationFrame(step);
    }, [isActive, target, duration]);
    return count;
};

const stats = [
    { value: "500+", label: "Leads Generated" },
    { value: "15+", label: "Clients Served" },
    { value: "6+", label: "Countries" },
    { value: "98%", label: "Data Accuracy" },
];

const StatItem = ({ value, label, isActive }) => {
    const animated = useCountUp(value, isActive);
    return (
        <div className="hero-stat">
            <span className="hero-stat__value">{animated}</span>
            <span className="hero-stat__label">{label}</span>
        </div>
    );
};

// Floating particle
const Particle = ({ style }) => <div className="hero-particle" style={style} />;

function Home() {
    const [heroVisible, setHeroVisible] = useState(false);
    const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
    const heroRef = useRef(null);

    // Trigger entrance animations
    useEffect(() => {
        const t = setTimeout(() => setHeroVisible(true), 100);
        return () => clearTimeout(t);
    }, []);

    // Subtle parallax on mouse move
    useEffect(() => {
        const handler = (e) => {
            setMousePos({
                x: (e.clientX / window.innerWidth - 0.5) * 20,
                y: (e.clientY / window.innerHeight - 0.5) * 20,
            });
        };
        window.addEventListener("mousemove", handler);
        return () => window.removeEventListener("mousemove", handler);
    }, []);

    // Generate particles once
    const particles = Array.from({ length: 18 }, (_, i) => ({
        left: `${Math.random() * 100}%`,
        top: `${Math.random() * 100}%`,
        width: `${2 + Math.random() * 4}px`,
        height: `${2 + Math.random() * 4}px`,
        animationDelay: `${Math.random() * 6}s`,
        animationDuration: `${5 + Math.random() * 8}s`,
        opacity: Math.random() * 0.5 + 0.1,
    }));

    return (

        <div className="home-page">

            {/* ════════════════════════════════
          HERO HEADER

      ════════════════════════════════ */}
            <header className={`hero ${heroVisible ? "hero--visible" : ""}`} ref={heroRef}>

                {/* Background layers */}
                <div className="hero__bg" />
                <div
                    className="hero__bg-orb hero__bg-orb--1"
                    style={{ transform: `translate(${mousePos.x * 0.6}px, ${mousePos.y * 0.6}px)` }}
                />
                <div
                    className="hero__bg-orb hero__bg-orb--2"
                    style={{ transform: `translate(${-mousePos.x * 0.4}px, ${-mousePos.y * 0.4}px)` }}
                />
                <div className="hero__grid" />

                {/* Floating particles */}
                {particles.map((p, i) => <Particle key={i} style={p} />)}

                {/* Content */}
                <div className="container hero__content">

                    {/* Badge */}
                    <div className="hero__badge" style={{ "--delay": "0s" }}>
                        <span className="hero__badge-dot" />
                        Data-Driven Lead Generation
                    </div>

                    {/* Headline */}
                    <h2 className="hero__welcome" style={{ "--delay": "0.15s" }}>
                        Welcome To
                    </h2>
                    <h1 className="hero__title" style={{ "--delay": "0.3s" }}>
                        Leads<span className="hero__title-accent">Q</span>Research
                    </h1>

                    {/* Divider line */}
                    <div className="hero__line" style={{ "--delay": "0.45s" }} />

                    {/* Description */}
                    <p className="hero__desc" style={{ "--delay": "0.55s" }}>
                        Welcome to LeadsQResearch, where innovation meets expertise in lead
                        generation for businesses of all sizes. We're not just another lead
                        generation company — we're a dynamic startup driven by a passion for
                        helping businesses grow and succeed in today's competitive landscape.
                    </p>

                    {/* CTA buttons */}
                    <div className="hero__ctas" style={{ "--delay": "0.7s" }}>
                        <Link to="/courses" className="hero-btn hero-btn--primary">
                            Our Services
                            <span className="hero-btn__arrow">→</span>
                        </Link>
                        <Link to="/contact" className="hero-btn hero-btn--outline">
                            Contact Us
                        </Link>
                    </div>

                    {/* Stats row */}
                    <div className="hero__stats" style={{ "--delay": "0.9s" }}>
                        {stats.map((s) => (
                            <StatItem key={s.label} value={s.value} label={s.label} isActive={heroVisible} />
                        ))}
                    </div>

                </div>

                {/* Scroll cue */}
                <div className="hero__scroll-cue">
                    <span className="hero__scroll-text">Scroll</span>
                    <div className="hero__scroll-line" />
                </div>

            </header>

            {/* ════════════════════════════════
          REST OF PAGE (unchanged)
      ════════════════════════════════ */}
            <section>
                <ChooseSection />
            </section>

            <SectionSeparator />

            <section>
                <TestimonialCarousel />
            </section>

            {/* <SectionSeparator /> */}

        </div>
    );
}

export default Home;
